import React, { useState } from 'react';
import { Box, Paper, Typography, useTheme, Select, MenuItem, FormControl, InputLabel, Chip, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import { ResponsiveBar } from '@nivo/bar';
import { EmptyState } from '../../../../components/global';

const ProductionComparison = () => {
  const theme = useTheme();
  const [selectedProductions, setSelectedProductions] = useState([]);

  const { data: productions } = useSWR('/productions');
  const { data, error, isLoading } = useSWR(
    selectedProductions.length > 0
      ? `/reports/production-comparison?productionIds=${selectedProductions.join(',')}`
      : '/reports/production-comparison',
    async (url) => {
      const response = await api.get(url);
      return response;
    }
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Σύγκριση Παραγωγών" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const comparison = data || [];

  const chartData = comparison.map((prod) => ({
    production: prod.productionName.length > 15 ? prod.productionName.substring(0, 15) + '...' : prod.productionName,
    budget: prod.budget,
    actual: prod.actualExpenses,
    predicted: prod.predictedExpenses,
  }));

  const columns = [
    {
      field: 'productionName',
      headerName: 'Παραγωγή',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'totalEpisodes',
      headerName: 'Επεισόδια',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
    },
    {
      field: 'budget',
      headerName: 'Προϋπολογισμός',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'actualExpenses',
      headerName: 'Πραγματικά',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'predictedExpenses',
      headerName: 'Προβλέψεις',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'totalExpenses',
      headerName: 'Σύνολο',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'costPerEpisode',
      headerName: 'Κόστος/Επεισόδιο',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'budgetPerEpisode',
      headerName: 'Προϋπολ./Επεισόδιο',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'utilization',
      headerName: 'Χρήση %',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value;
        const getStatusColor = () => {
          if (value > 100) return theme.palette.error.main;
          if (value > 80) return theme.palette.warning.main;
          return theme.palette.success.main;
        };
        
        return (
          <Box sx={{ width: '100%', px: 1.5, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(value, 100)}
                  sx={{
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: theme.palette.action.hover,
                    overflow: 'hidden',
                    boxShadow: `inset 0 1px 2px ${theme.palette.divider}`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getStatusColor(),
                      borderRadius: 2,
                      transition: 'all 0.3s ease-in-out',
                      boxShadow: `0 0 8px ${getStatusColor()}40`,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: getStatusColor(),
                  fontSize: '0.95rem',
                  minWidth: '45px',
                  textAlign: 'right',
                }}
              >
                {value.toFixed(1)}%
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'variance',
      headerName: 'Απόκλιση Έξοδων (+/-)',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value;
        // Positive variance (budget > totalExpenses) = under budget = good (green)
        // Negative variance (budget < totalExpenses) = over budget = bad (red)
        const color = value < 0 ? theme.palette.error.main : theme.palette.success.main;
        return (
          <Typography sx={{ color, fontWeight: 600 }}>
            {formatCurrency(value)}
          </Typography>
        );
      },
    },
    {
      field: 'efficiency',
      headerName: 'Αποδοτικότητα',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.value;
        return `${value.toFixed(1)}%`;
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Σύγκριση Παραγωγών" module="Αναφορές" subtitle="Σύγκριση παραγωγών side-by-side" />

      {/* Production Selector */}
      {productions && productions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ maxWidth: 600 }}>
            <InputLabel>Επιλογή Παραγωγών (Πατήστε Ctrl για πολλαπλή επιλογή)</InputLabel>
            <Select
              multiple
              value={selectedProductions}
              onChange={(e) => setSelectedProductions(e.target.value)}
              label="Επιλογή Παραγωγών"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const prod = productions.find(p => p.id === value);
                    return <Chip key={value} label={prod?.p_descr || value} size="small" />;
                  })}
                </Box>
              )}
            >
              {productions.map((prod) => (
                <MenuItem key={prod.id} value={prod.id}>
                  {prod.p_descr}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3, height: 450, boxShadow: theme.shadows[3] }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Σύγκριση Προϋπολογισμού & Εξόδων
          </Typography>
          <Box sx={{ height: 380 }}>
            <ResponsiveBar
              data={chartData}
              keys={['budget', 'actual', 'predicted']}
              indexBy="production"
              margin={{ top: 20, right: 80, bottom: 80, left: 80 }}
              padding={0.3}
              valueFormat={formatCurrency}
              colors={[theme.palette.success.main, theme.palette.info.main, theme.palette.warning.main]}
              axisLeft={{
                format: (value) => formatCurrency(value),
              }}
              axisBottom={{
                tickRotation: -45,
              }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'top-right',
                  direction: 'column',
                  translateX: 100,
                  itemWidth: 80,
                  itemHeight: 20,
                },
              ]}
              theme={{
                text: {
                  fill: theme.palette.text.primary,
                  fontSize: 12,
                },
                grid: {
                  line: {
                    stroke: theme.palette.divider,
                  },
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Data Grid */}
      <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
        <FormListToolbar disableAdd />
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={comparison.map((c, index) => ({ ...c, id: index }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductionComparison;

